/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorPlugins } from '../types/EditorPlugins';

type Toggle = {
	hasToggle?: boolean;
	toggleLabel?: string;
	toggleValue?: boolean;
	toggleFeatures?: ReadonlyArray<Feature>;
};

export type Capability = {
	name: string;
	description: string;
	features: ReadonlyArray<Feature>;
	isSingleFeatureChoice: boolean;
	defaultFeature?: string;
} & Toggle;

export type Feature = {
	name: string;
	description: string;
	plugins: Readonly<EditorPlugins>;
	isSinglePluginChoice: boolean;
	defaultPlugin?: string;
	toggleFeatureName?: string;
};

export const capabilities = [
	getProductivityCapability(),
	getAiCapability(),
	getCollaborationCapability(),
	getFileManagementCapability(),
	getContentConversionAndEmbeddingCapability(),
	getTextFormattingCapability(),
	getContentAndBlockFormattingCapability(),
	getImagesCapability(),
	getListsCapability(),
	getTablesCapability(),
	getPageManagementCapability(),
	getAdvancedHtmlEditingCapability(),
	getOtherCapability()
] as const satisfies ReadonlyArray<Capability>;

// The `PluginName` does not contain hidden plugins. Hence, it's useful in tests only.
export type PluginName = typeof capabilities[ number ][ 'features' ][ number ][ 'plugins' ][ number ] |
	ReturnType<typeof getCollaborationCapability>[ 'toggleFeatures' ][ number ][ 'plugins' ][ number ];

export type FeatureName = typeof capabilities[ number ][ 'features' ][ number ][ 'name' ] |
	ReturnType<typeof getCollaborationCapability>[ 'toggleFeatures' ][ number ][ 'name' ];

export type CapabilityName = typeof capabilities[ number ][ 'name' ];

/* eslint-disable max-len */

function getProductivityCapability() {
	return {
		name: 'Productivity',
		description: 'A collection of advanced document features to increase user efficiency and workflow organization.',
		features: [
			{
				name: 'Format Painter',
				description: 'Allows for copying text formats and applying it to another part of the content.',
				plugins: [
					'FormatPainter'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Slash Commands',
				description: 'Easy access to a predefined list of editor commands by typing / inside the editor.',
				plugins: [
					'SlashCommand'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Templates',
				description: 'Provides inserting predefined content templates into the document.',
				plugins: [
					'Template'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Case Change',
				description: 'Quickly change the letter cases of the selected piece of text.',
				plugins: [
					'CaseChange'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Enhanced Paste from Office',
				description: 'A more advanced paste transforming advanced and complex content from Microsoft Word and Excel to a valid and improved structure which can be understood by the editor features.',
				plugins: [
					'PasteFromOfficeEnhanced'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Merge Fields',
				description: 'Allows you to insert distinctive in-content placeholder items to create document templates. Type {{ to select a field from a list.',
				plugins: [
					'MergeFields'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Find and Replace',
				description: 'Enables finding specific word or phrase in the content, as well as replacing it with another.',
				plugins: [
					'FindAndReplace'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Fullscreen',
				description: 'Allows users to temporarily expand the editor to the whole browser viewport, giving them more space to edit content comfortably and use the editor’s UI features.',
				plugins: [
					'Fullscreen'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Emoji',
				description: 'Quickly insert emojis by typing ":". An advanced emoji picker is also available for searching and selecting emojis.',
				plugins: [
					'Emoji'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Special Characters',
				description: 'Allows for inserting special characters to the content. Supports several predefined sets such as currency, arrows, and mathematical symbols.',
				plugins: [
					'SpecialCharactersArrows',
					'SpecialCharactersCurrency',
					'SpecialCharactersEssentials',
					'SpecialCharactersLatin',
					'SpecialCharactersMathematical',
					'SpecialCharactersText'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Autoformatting',
				description: 'Automatically formats text by typing Markdown-like code to carry out predefined formatting actions. For example, surrounding a text with ** will make it bolded.',
				plugins: [
					'Autoformat'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Automatic Text Transformation',
				description: 'Automatically turns predefined snippets into their typographically improved forms, for example (c) will transform to ©.',
				plugins: [
					'TextTransformation'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getAiCapability() {
	return {
		name: 'AI',
		description: 'Enables an AI assistant to make complex content creation and revision tasks easier and more efficient. By default, it uses an OpenAI model, but you can set up the assistant with other models and APIs.',
		features: [
			{
				name: 'AI Assistant',
				description: 'Introduces AI tools for generating and improving content via pre-defined commands or custom user queries.',
				plugins: [
					'OpenAITextAdapter',
					'AWSTextAdapter'
				],
				isSinglePluginChoice: true,
				defaultPlugin: 'OpenAITextAdapter'
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getCollaborationCapability() {
	return {
		name: 'Collaboration',
		description: 'A collection of real-time collaboration (RTC) or asynchronous collaboration features for editing, commenting, tracking changes, and viewing revision history, fostering effective teamwork. Use the toggle to switch between RTC and non-RTC.',
		'hasToggle': true,
		'toggleLabel': 'Real-time',
		'toggleValue': true,
		features: [
			{
				name: 'Real-time Collaborative Editing (Co-Authoring)',
				description: 'Allows multiple users to edit the content at the same time. Shows the actions of other users in real time.',
				plugins: [
					'RealTimeCollaborativeEditing',
					'PresenceList'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Real-time Collaborative Comments',
				description: 'Allows for commenting on the content, replying to the comments, and creating discussion threads with multiple users in real time.',
				plugins: [
					'RealTimeCollaborativeComments'
				],
				isSinglePluginChoice: false,
				toggleFeatureName: 'Comments'
			},
			{
				name: 'Real-time Collaborative Track Changes',
				description: 'Enables the track changes mode (aka “suggestion mode”), so multiple users can make suggestions on the content in real time.',
				plugins: [
					'RealTimeCollaborativeTrackChanges'
				],
				isSinglePluginChoice: false,
				toggleFeatureName: 'Track Changes'
			},
			{
				name: 'Real-time Collaborative Revision History',
				description: 'Allows users to create and view the chronological revision history of their content with the possibility to compare or restore any given revisions of the document in real time.',
				plugins: [
					'RealTimeCollaborativeRevisionHistory'
				],
				isSinglePluginChoice: false,
				toggleFeatureName: 'Revision History'
			},
			{
				name: 'Mentions',
				description: 'Provides a preconfigured marker that allows users to utilize autocomplete suggestions for mentions and tags. Use @ to mention someone, and # to add a tag.',
				plugins: [
					'Mention'
				],
				isSinglePluginChoice: false,
				toggleFeatureName: 'Mentions'
			}
		],
		'toggleFeatures': [
			{
				name: 'Comments',
				description: 'Allows to add comments to any part of the rich-text content, including text and block elements. You can edit, delete and reply to comments, creating asynchronous discussion threads.',
				plugins: [
					'Comments'
				],
				isSinglePluginChoice: false,
				toggleFeatureName: 'Real-time Collaborative Comments'
			},
			{
				name: 'Track Changes',
				description: 'Enables the track changes mode (aka “suggestion mode”). Changes made in this mode are marked in the content and shown in the sidebar as suggestions that can be accepted or discarded by users asynchronously.',
				plugins: [
					'TrackChanges'
				],
				isSinglePluginChoice: false,
				toggleFeatureName: 'Real-time Collaborative Track Changes'
			},
			{
				name: 'Revision History',
				description: 'Allows users to create and view the chronological revision history of their content with the possibility to compare or restore any given revisions of the document.',
				plugins: [
					'RevisionHistory'
				],
				isSinglePluginChoice: false,
				toggleFeatureName: 'Real-time Collaborative Revision History'
			},
			{
				name: 'Mentions',
				description: 'Provides a preconfigured marker that lets users utilize autocomplete suggestions for mentions and tags. Use @ to mention someone, and # to add a tag.',
				plugins: [
					'Mention'
				],
				isSinglePluginChoice: false,
				toggleFeatureName: 'Mentions'
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getFileManagementCapability() {
	return {
		name: 'File Management',
		description: 'A collection of tools that enable users to easily upload, store, retrieve, and organize their files.',
		features: [
			{
				name: 'CKBox',
				description: 'An asset manager that offers efficient management of files, allows users to insert images and link to files in their content. It also provides responsive images and image editing capabilities to enhance document accessibility.',
				plugins: [
					'CKBox',
					'CKBoxImageEdit'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Uploadcare',
				description: 'Image uploader and editor that lets you easily upload images from various services like Dropbox, Google Drive, OneDrive or Facebook and insert them into your content. It offers an image editor with multiple editing options, optimization capabilities and provides a responsive images mechanism.',
				plugins: [
					'Uploadcare',
					'UploadcareImageEdit'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Simple Upload Adapter',
				description: 'Allows users to upload images to the server using the XMLHttpRequest API with a minimal editor configuration.',
				plugins: [
					'SimpleUploadAdapter'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Base64 Upload Adapter',
				description: 'Converts inserted images into Base64-encoded strings in the editor output.',
				plugins: [
					'Base64UploadAdapter'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: true,
		defaultFeature: 'CKBox'
	} as const satisfies Capability;
}

function getContentConversionAndEmbeddingCapability() {
	return {
		name: 'Content Conversion & Embedding',
		description: 'A collection of features that enables easy conversion between HTML, Word, and PDF, enhances pasting from Office with preserved formatting, and simplifies incorporating external content like media or Markdown, boosting versatility and compatibility in content handling.',
		features: [
			{
				name: 'Export to Word',
				description: 'Allows for generating a Word file directly from the editor content.',
				plugins: [
					'ExportWord'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Export to PDF',
				description: 'Allows for generating a PDF file that can be printed directly from the editor content.',
				plugins: [
					'ExportPdf'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Import from Word',
				description: 'Allows for importing content from a Word file directly into CKEditor 5.',
				plugins: [
					'ImportWord'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Paste from Office',
				description: 'Allows transforming pasted content from Microsoft Office or Google Docs to a valid structure which can be understood by the editor features.',
				plugins: [
					'PasteFromOffice'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Media Embed',
				description: 'Allows for inserting embeddable media such as YouTube and Vimeo videos, Instagram posts and tweets or Google Maps into your rich-text content.',
				plugins: [
					'MediaEmbed'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Markdown  Input and Output',
				description: 'Produces Markdown output straight from the editor content.',
				plugins: [
					'Markdown'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Paste Markdown (experimental)',
				description: 'Lets users paste Markdown-formatted content straight into the editor.',
				plugins: [
					'PasteFromMarkdownExperimental'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Export with Inline Styles',
				description: 'The export with inline styles feature applies the styles defined by CSS style sheets directly on HTML elements. This is especially useful for email creation but may be helpful in other use cases.',
				plugins: [
					'ExportInlineStyles'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getTextFormattingCapability() {
	return {
		name: 'Text Formatting',
		description: 'Empowers users to effectively create emphasized and visually appealing content, enhancing readability and impact.',
		features: [
			{
				name: 'Bold',
				description: 'Implements bold formatting support. It is a part of the basic text styles package.',
				plugins: [
					'Bold'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Italic',
				description: 'Implements italic formatting support. It is a part of the basic text styles package.',
				plugins: [
					'Italic'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Underline',
				description: 'Implements underline formatting support. It is a part of the basic text styles package.',
				plugins: [
					'Underline'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Strikethrough',
				description: 'Implements strikethrough formatting support. It is a part of the basic text styles package.',
				plugins: [
					'Strikethrough'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Inline Code',
				description: 'Implements inline code formatting support. It is a part of the basic text styles package.',
				plugins: [
					'Code'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Subscript',
				description: 'Implements subscript formatting support. It is a part of the basic text styles package.',
				plugins: [
					'Subscript'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Superscript',
				description: 'Implements superscript formatting support. It is a part of the basic text styles package.',
				plugins: [
					'Superscript'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Font Styles',
				description: 'Provides font size and family support, as well as font color and background color support with a configurable color palette panel.',
				plugins: [
					'FontBackgroundColor',
					'FontColor',
					'FontFamily',
					'FontSize'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Remove Formatting',
				description: 'Allows for a quick removal of any text formatting applied via HTML elements or CSS styles.',
				plugins: [
					'RemoveFormat'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Highlight',
				description: 'Allows for marking text with pen and marker tools, especially useful when reviewing or highlighting content for future reference.',
				plugins: [
					'Highlight'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getContentAndBlockFormattingCapability() {
	return {
		name: 'Content & Block Formatting',
		description: 'A collection of features that enhances content versatility and expressiveness, enabling the creation of more engaging and organized content.',
		features: [
			{
				name: 'Headings',
				description: 'Allows for creating headings in your content. The heading levels are configurable.',
				plugins: [
					'Heading'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Links',
				description: 'Allows for inserting hyperlinks into the content and offers the UI to create and edit them.',
				plugins: [
					'Link',
					'AutoLink'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Bookmarks',
				description: 'Allows for adding and managing bookmarks (anchors) attached to the content.',
				plugins: [
					'Bookmark'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Block Quotes',
				description: 'Allows for inserting block quotes to easily include quotations and passages in the rich-text content.',
				plugins: [
					'BlockQuote'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Horizontal Line',
				description: 'Allows for inserting horizontal rules into your content.',
				plugins: [
					'HorizontalLine'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Code Blocks',
				description: 'Allows for inserting and editing of blocks of pre–formatted code with an assigned programming language.',
				plugins: [
					'CodeBlock'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Indentation',
				description: 'Allows for controlling the indentation of elements such as paragraphs, headings, or lists.',
				plugins: [
					'Indent',
					'IndentBlock'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Text Alignment',
				description: 'Allows for left, right, centered, or justified text alignment.',
				plugins: [
					'Alignment'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Styles',
				description: 'Allows applying pre-configured styles to editor content.',
				plugins: [
					'Style'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getImagesCapability() {
	return {
		name: 'Images',
		description: 'Versatile image handling, enabling easy insertion, editing, and customization to enrich document visuals and accessibility.',
		features: [
			{
				name: 'Inline Images',
				description: 'Allows for inserting images inside blocks of text, such as paragraphs, headers or block quotes. ',
				plugins: [
					'ImageInline'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Block Images',
				description: 'Allows for inserting images between the blocks of text, like paragraphs or block quotes.',
				plugins: [
					'ImageBlock'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Image Resize',
				description: 'Allows for resizing images using handles.',
				plugins: [
					'ImageResize'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Image optimizer by CKBox',
				description: 'Allows for editing images - cropping, resizing, flipping and mirroring.',
				plugins: [
					'CKBoxImageEdit'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Image optimizer by Uploadcare',
				description: 'Allows for image editing - cropping, resizing, flipping, mirroring and adjusting image parameters (like contrast, brightness, saturation).',
				plugins: [
					'UploadcareImageEdit'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Image Upload',
				description: 'Allows for inserting images via source URL, pasting from the clipboard, dragging and dropping, and selecting through a file system dialog or from a media management tool.',
				plugins: [
					'ImageUpload'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Image Styles',
				description: 'Allows for using predefined, configurable styles to display the images.',
				plugins: [
					'ImageStyle'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Block Image Linking',
				description: 'Allows for adding clickable links to block images for creating banners or referrers.',
				plugins: [
					'LinkImage'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Image Insert via URL',
				description: 'Allows for inserting images via source URL.',
				plugins: [
					'ImageInsertViaUrl',
					'AutoImage'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Image Captions',
				description: 'Allows for adding captions to block images to give additional context and boost accessibility.',
				plugins: [
					'ImageCaption'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Image Alt Text',
				description: 'Allows for adding text alternatives to images to give additional context and boost accessibility.',
				plugins: [
					'ImageTextAlternative'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getListsCapability() {
	return {
		name: 'Lists',
		description: 'A collection of features to create and customize different types of lists, enhancing both structure and functionality in documents.',
		features: [
			{
				name: 'Lists',
				description: 'Allows for creating bulleted and numbered lists. Supports block content inside list elements.',
				plugins: [
					'List'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'To-do Lists',
				description: 'Allows for creating a list of interactive checkboxes with labels. Can be combined with bulleted and numbered lists.',
				plugins: [
					'TodoList'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'List Styles & Properties',
				description: 'Allows for styling list markers (symbols, letters or digits) to display at each list level and setting the start index as well as setting list reversal for numbered lists.',
				plugins: [
					'ListProperties'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Multi-level Lists',
				description: 'Allows for inserting legal-style numbered lists with complex hierarchy and organization.',
				plugins: [
					'MultiLevelList'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getTablesCapability() {
	return {
		name: 'Tables',
		description: 'Enables complex data presentation through robust table creation and customization, including adjustable column resizing and detailed cell property controls, topped with captioning features for clarity and context.',
		features: [
			{
				name: 'Tables',
				description: 'Allows for table creation and editing to help authors bring tabular data into their content.',
				plugins: [
					'Table'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Layout Tables',
				description: 'Layout tables are used to structure web page content spatially rather than for presenting tabular data. They allow integrators to create multi-column designs and precise positioning of elements on a page.',
				plugins: [
					'TableLayout'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Plain Table Output',
				description: 'This feature strips the <figure> tag from the table data. This is because this tag is not supported by most popular email clients and removing it ensures compatibility.',
				plugins: [
					'PlainTableOutput'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Table Styles & Properties',
				description: 'Allows for styling entire tables.',
				plugins: [
					'TableProperties'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Table Cell Styles & Properties',
				description: 'Allows for styling individual table cells.',
				plugins: [
					'TableCellProperties'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Column Resize',
				description: 'Allows for table column resize using a resize handle.',
				plugins: [
					'TableColumnResize'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Table Captions',
				description: 'Allows for adding table captions, which informs the reader about the table content and is beneficial for accessibility.',
				plugins: [
					'TableCaption'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getPageManagementCapability() {
	return {
		name: 'Page Management',
		description: 'Enables page view and organization functionalities to facilitate clear, well-organized content layout.',
		features: [
			{
				name: 'Document Outline',
				description: 'Enables a navigable display of document sections (headings) next to the editor.',
				plugins: [
					'DocumentOutline'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Table Of Contents',
				description: 'Allows for inserting a widget with a list of section titles (headings) into the editor content.',
				plugins: [
					'TableOfContents'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Pagination',
				description: 'Allows users to see where the page breaks would be after the document is exported to a PDF or Word file. ',
				plugins: [
					'Pagination'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Page Breaks',
				description: 'Allows users to insert a page break so content can be structured better for printing.',
				plugins: [
					'PageBreak'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Content Minimap',
				description: 'Enables a miniature display of the content, next to the editor to aid navigation.',
				plugins: [
					'Minimap'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getAdvancedHtmlEditingCapability() {
	return {
		name: 'Advanced HTML Editing',
		description: 'Offers a direct HTML interaction with seamless integration and customization of web content for a more flexible and precise editing experience.',
		features: [
			{
				name: 'Show Blocks',
				description: 'Allows to display the HTML content elements as blocks with corresponding element names.',
				plugins: [
					'ShowBlocks'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Enhanced Source Code Editing',
				description: 'Allows for viewing and editing the source code of the document (HTML, Markdown, etc.) in a handy modal window (compatible with all editor types) with syntax highlighting, autocompletion and more.',
				plugins: [
					'SourceEditingEnhanced'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Source Code Editing',
				description: 'Allows for viewing and editing the source code of the document (HTML, Markdown, etc.)',
				plugins: [
					'SourceEditing'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'General HTML Support',
				description: 'Allows for enabling HTML features that are not yet supported by dedicated plugins.',
				plugins: [
					'GeneralHtmlSupport'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'HTML Embed',
				description: 'Allows for embedding an arbitrary HTML snippet into the editor content.',
				plugins: [
					'HtmlEmbed'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'HTML Comment Element',
				description: 'Preserves the HTML comments in the editor data.',
				plugins: [
					'HtmlComment'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Full page HTML',
				description: 'Allows for editing entire HTML pages, including the page metadata.',
				plugins: [
					'FullPage'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

function getOtherCapability() {
	return {
		name: 'Other',
		description: 'Various miscellaneous features that do not fall into other categories.',
		features: [
			{
				name: 'Text Part Language',
				description: 'Allows for assigning the language and direction of selected text fragments, supporting multilingual documents.',
				plugins: [
					'TextPartLanguage'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Word and character count',
				description: 'Provides the possibility to track the number of words and characters written.',
				plugins: [
					'WordCount'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Document Title',
				description: 'Allows for adding the title field to your content. It ensures that there will always be a single title field at the beginning of the content.',
				plugins: [
					'Title'
				],
				isSinglePluginChoice: false
			},
			{
				name: 'Email Configuration Helper',
				description: 'The email configuration helper plugin is the best way to make configuring an email editor more manageable.',
				plugins: [
					'EmailConfigurationHelper'
				],
				isSinglePluginChoice: false
			}
		],
		isSingleFeatureChoice: false
	} as const satisfies Capability;
}

/* eslint-enable max-len */
